import { useState } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Search, ChevronLeft, ChevronRight, Eye, Plus, Pencil, Trash2, Upload, Printer, FilePenLine, Download, FileText } from "lucide-react";

const PAGE_SIZE = 50;

const TIPO_LABELS: Record<string, string> = {
  inicio_turno: "Início de Turno",
  fim_turno: "Fim de Turno",
  inicio_almoco: "Início de Almoço",
  fim_almoco: "Fim de Almoço",
};

const TIPO_COLORS: Record<string, string> = {
  inicio_turno: "bg-green-100 text-green-800",
  fim_turno: "bg-red-100 text-red-800",
  inicio_almoco: "bg-yellow-100 text-yellow-800",
  fim_almoco: "bg-blue-100 text-blue-800",
};

const TIPO_OCORRENCIA_LABELS: Record<string, string> = {
  justificativa: "Justificativa",
  declaracao_horas: "Declaração de Horas",
  atestado: "Atestado",
  ferias: "Férias",
  folga: "Folga",
  feriado: "Feriado",
};

const MOTIVO_AJUSTE_LABELS: Record<string, string> = {
  erro_horario: "Erro de horário",
  tipo_incorreto: "Tipo incorreto",
  data_incorreta: "Data incorreta",
  batida_duplicada_indevida: "Batida duplicada/indevida",
  outro: "Outro",
};

const DIAS_TRABALHO_LABELS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

function competenciaAtual() {
  const agora = new Date();
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, "0")}`;
}

function formatarHoras(minutos: number | null | undefined): string {
  const total = Math.abs(Number(minutos ?? 0));
  const horas = Math.floor(total / 60);
  const minutosRestantes = total % 60;
  return `${Number(minutos ?? 0) < 0 ? "−" : ""}${horas}h ${String(minutosRestantes).padStart(2, "0")}min`;
}

function formatarDataHora(dt: string | Date | null): string {
  if (!dt) return "—";
  const d = new Date(dt);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatarCpf(cpf: string): string {
  const n = cpf.replace(/\D/g, "");
  if (n.length !== 11) return cpf;
  return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}`;
}

function dataParaInput(dt: string | Date): string {
  const d = new Date(dt);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function horaParaInput(dt: string | Date): string {
  const d = new Date(dt);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function diasTrabalhoParaFormulario(valor: unknown): number[] {
  try {
    const dias = Array.isArray(valor) ? valor : JSON.parse(String(valor ?? "[]"));
    return Array.isArray(dias) ? dias.filter((dia) => Number.isInteger(dia) && dia >= 0 && dia <= 6) : [];
  } catch {
    return [];
  }
}

export default function ControlePonto() {
  const { user } = useGoogleAuth();
  const utils = trpc.useUtils();
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [tipo, setTipo] = useState("todos");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [funcionarioOcorrenciaId, setFuncionarioOcorrenciaId] = useState("todos");
  const [tipoOcorrenciaFiltro, setTipoOcorrenciaFiltro] = useState("todos");
  const [page, setPage] = useState(1);
  const [exportando, setExportando] = useState<"pdf" | "excel" | null>(null);
  const [fotoModal, setFotoModal] = useState<{ url: string; nome: string } | null>(null);
  const [aba, setAba] = useState("registros");
  const [ocorrenciaModal, setOcorrenciaModal] = useState(false);
  const [ocorrenciaForm, setOcorrenciaForm] = useState({
    id: null as number | null,
    emitenteId: "",
    funcionarioId: "",
    tipo: "justificativa",
    dataInicio: "",
    dataFim: "",
    horasAbonadas: "",
    observacao: "",
    arquivoUrl: "" as string | null,
    arquivo: null as File | null,
  });
  const [funcionarioFechamentoId, setFuncionarioFechamentoId] = useState("");
  const [competencia, setCompetencia] = useState(competenciaAtual());
  const [ajusteModal, setAjusteModal] = useState(false);
  const [quitacaoModal, setQuitacaoModal] = useState(false);
  const [quitacaoObservacao, setQuitacaoObservacao] = useState("");
  const [correcaoPendenciaModal, setCorrecaoPendenciaModal] = useState(false);
  const [correcaoPendenciaForm, setCorrecaoPendenciaForm] = useState({
    pendencia: null as any,
    cargaHorariaDiaria: "",
    diasTrabalho: [] as number[],
    registrosDesconsiderados: [] as number[],
    batidasFaltantes: {} as Record<string, string>,
    motivoAjuste: "erro_horario",
    detalhamentoAjuste: "",
  });
  const [ajusteForm, setAjusteForm] = useState({
    registro: null as any,
    tipoAjuste: "corrigir" as "corrigir" | "desconsiderar",
    tipoCorrigido: "inicio_turno",
    dataCorrigida: "",
    horaCorrigida: "",
    motivoAjuste: "erro_horario",
    detalhamentoAjuste: "",
    arquivo: null as File | null,
  });

  const queryParams = {
    search: search || undefined,
    tipo: tipo !== "todos" ? tipo : undefined,
    dataInicio: dataInicio || undefined,
    dataFim: dataFim || undefined,
    page,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading, refetch } = trpc.ponto.list.useQuery(queryParams, {
    enabled: !!user,
  });
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, { enabled: !!user });
  const { data: funcionariosAtivos = [] } = trpc.rh.funcionarios.listAtivos.useQuery(undefined, { enabled: !!user });
  const { data: ocorrencias = [], refetch: refetchOcorrencias } = trpc.ponto.ocorrencias.list.useQuery({
    funcionarioId: funcionarioOcorrenciaId !== "todos" ? Number(funcionarioOcorrenciaId) : undefined,
    tipo: tipoOcorrenciaFiltro !== "todos" ? tipoOcorrenciaFiltro as any : undefined,
    dataInicio: dataInicio || undefined,
    dataFim: dataFim || undefined,
  }, { enabled: !!user && aba === "ocorrencias" });
  const fechamentoInput = { funcionarioId: Number(funcionarioFechamentoId) || 0, competencia };
  const { data: apuracao, refetch: refetchApuracao } = trpc.ponto.fechamento.apurar.useQuery(fechamentoInput, {
    enabled: !!user && aba === "fechamento" && !!funcionarioFechamentoId,
  });
  const { data: fechamentoSalvo, refetch: refetchFechamento } = trpc.ponto.fechamento.get.useQuery(fechamentoInput, {
    enabled: !!user && aba === "fechamento" && !!funcionarioFechamentoId,
  });
  const criarOcorrenciaMut = trpc.ponto.ocorrencias.create.useMutation({
    onSuccess: () => { toast.success("Ocorrência salva"); refetchOcorrencias(); refetchApuracao(); refetchFechamento(); setOcorrenciaModal(false); },
    onError: (erro) => toast.error(erro.message),
  });
  const atualizarOcorrenciaMut = trpc.ponto.ocorrencias.update.useMutation({
    onSuccess: () => { toast.success("Ocorrência atualizada"); refetchOcorrencias(); refetchApuracao(); refetchFechamento(); setOcorrenciaModal(false); },
    onError: (erro) => toast.error(erro.message),
  });
  const excluirOcorrenciaMut = trpc.ponto.ocorrencias.delete.useMutation({
    onSuccess: () => { toast.success("Ocorrência excluída"); refetchOcorrencias(); refetchApuracao(); refetchFechamento(); },
    onError: (erro) => toast.error(erro.message),
  });
  const fecharMut = trpc.ponto.fechamento.fechar.useMutation({
    onSuccess: () => { toast.success("Competência fechada"); refetchApuracao(); refetchFechamento(); },
    onError: (erro) => toast.error(erro.message),
  });
  const reabrirMut = trpc.ponto.fechamento.reabrir.useMutation({
    onSuccess: () => { toast.success("Competência reaberta"); refetchApuracao(); refetchFechamento(); },
    onError: (erro) => toast.error(erro.message),
  });
  const quitarMut = trpc.ponto.fechamento.quitar.useMutation({
    onSuccess: () => { toast.success("Saldo quitado"); refetchApuracao(); refetchFechamento(); setQuitacaoModal(false); setQuitacaoObservacao(""); },
    onError: (erro) => toast.error(erro.message),
  });
  const criarAjusteMut = trpc.ponto.ajustes.create.useMutation({
    onSuccess: () => { toast.success("Ajuste de batida criado"); refetch(); refetchApuracao(); refetchFechamento(); setAjusteModal(false); },
    onError: (erro) => toast.error(erro.message),
  });
  const atualizarJornadaMut = trpc.rh.funcionarios.update.useMutation({
    onSuccess: () => { toast.success("Jornada configurada"); refetchApuracao(); refetchFechamento(); setCorrecaoPendenciaModal(false); },
    onError: (erro) => toast.error(erro.message),
  });
  const corrigirPendenciaMut = trpc.ponto.ajustes.corrigirPendencia.useMutation({
    onSuccess: () => { toast.success("Pendência corrigida"); refetch(); refetchApuracao(); refetchFechamento(); setCorrecaoPendenciaModal(false); },
    onError: (erro) => toast.error(erro.message),
  });

  const registros = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  function buscar() {
    setSearch(searchInput);
    setPage(1);
  }

  function limparFiltros() {
    setSearch("");
    setSearchInput("");
    setTipo("todos");
    setDataInicio("");
    setDataFim("");
    setPage(1);
  }

  function statusRegistroExportacao(registro: any) {
    if (registro.sobrepostoPorId) return `Sobreposta pelo ajuste #${registro.sobrepostoPorId}`;
    if (registro.origem === "ajuste" && registro.tipoAjuste === "desconsiderar") return "Desconsiderada";
    if (registro.origem === "ajuste") return "Ajuste ativo";
    return "Original ativa";
  }

  function localRegistroExportacao(registro: any) {
    return registro.latitude && registro.longitude
      ? `${Number(registro.latitude).toFixed(5)}, ${Number(registro.longitude).toFixed(5)}`
      : "—";
  }

  async function carregarRegistrosParaExportacao() {
    if (total === 0) throw new Error("Não há registros para exportar.");
    const resultado = await utils.ponto.list.fetch({
      search: search || undefined,
      tipo: tipo !== "todos" ? tipo : undefined,
      dataInicio: dataInicio || undefined,
      dataFim: dataFim || undefined,
      page: 1,
      pageSize: total,
    });
    return resultado.data;
  }

  function linhasExportacao(registrosParaExportar: any[]) {
    return registrosParaExportar.map((registro) => ({
      Nome: registro.nome,
      Matrícula: registro.matricula,
      CPF: formatarCpf(registro.cpf),
      Tipo: TIPO_LABELS[registro.tipo] ?? registro.tipo,
      Status: statusRegistroExportacao(registro),
      "Data / Hora": formatarDataHora(registro.criadoEm),
      Local: localRegistroExportacao(registro),
    }));
  }

  async function exportarExcelRegistros() {
    setExportando("excel");
    try {
      const registrosParaExportar = await carregarRegistrosParaExportacao();
      const XLSX = await import("xlsx");
      const planilha = XLSX.utils.json_to_sheet(linhasExportacao(registrosParaExportar));
      planilha["!cols"] = [{ wch: 32 }, { wch: 16 }, { wch: 16 }, { wch: 20 }, { wch: 28 }, { wch: 22 }, { wch: 20 }];
      const arquivo = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(arquivo, planilha, "Registros de Ponto");
      XLSX.writeFile(arquivo, `registros_ponto_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`${registrosParaExportar.length} registro(s) exportado(s) para Excel.`);
    } catch (erro: any) {
      toast.error(erro.message ?? "Erro ao exportar registros para Excel.");
    } finally {
      setExportando(null);
    }
  }

  async function exportarPdfRegistros() {
    setExportando("pdf");
    try {
      const registrosParaExportar = await carregarRegistrosParaExportacao();
      const linhas = linhasExportacao(registrosParaExportar);
      const { default: jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;
      const documento = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      documento.setFontSize(16);
      documento.text("Registros de Ponto", 14, 15);
      documento.setFontSize(10);
      documento.text(`Gerado em: ${new Date().toLocaleString("pt-BR")} · Registros filtrados: ${linhas.length}`, 14, 22);
      autoTable(documento, {
        head: [["Nome", "Matrícula", "CPF", "Tipo", "Status", "Data / Hora", "Local"]],
        body: linhas.map((linha) => [linha.Nome, linha.Matrícula, linha.CPF, linha.Tipo, linha.Status, linha["Data / Hora"], linha.Local]),
        startY: 28,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [31, 41, 55] },
      });
      documento.save(`registros_ponto_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(`${registrosParaExportar.length} registro(s) exportado(s) para PDF.`);
    } catch (erro: any) {
      toast.error(erro.message ?? "Erro ao exportar registros para PDF.");
    } finally {
      setExportando(null);
    }
  }

  function limparFiltrosOcorrencias() {
    setFuncionarioOcorrenciaId("todos");
    setTipoOcorrenciaFiltro("todos");
    setDataInicio("");
    setDataFim("");
  }

  function abrirNovaOcorrencia(ocorrencia?: any) {
    const funcionario = (funcionariosAtivos as any[]).find((item) => item.id === ocorrencia?.funcionarioId);
    setOcorrenciaForm({
      id: ocorrencia?.id ?? null,
      emitenteId: String(ocorrencia?.emitenteId ?? funcionario?.emitenteId ?? (emitentes as any[])[0]?.id ?? ""),
      funcionarioId: ocorrencia?.funcionarioId ? String(ocorrencia.funcionarioId) : "",
      tipo: ocorrencia?.tipo ?? "justificativa",
      dataInicio: ocorrencia?.dataInicioTexto ?? (ocorrencia?.dataInicio ? String(ocorrencia.dataInicio).slice(0, 10) : ""),
      dataFim: ocorrencia?.dataFimTexto ?? (ocorrencia?.dataFim ? String(ocorrencia.dataFim).slice(0, 10) : ""),
      horasAbonadas: ocorrencia?.horasAbonadas != null ? String(ocorrencia.horasAbonadas) : "",
      observacao: ocorrencia?.observacao ?? "",
      arquivoUrl: ocorrencia?.arquivoUrl ?? "",
      arquivo: null,
    });
    setOcorrenciaModal(true);
  }

  async function salvarOcorrencia() {
    if (!ocorrenciaForm.emitenteId || !ocorrenciaForm.dataInicio || !ocorrenciaForm.dataFim || ocorrenciaForm.horasAbonadas === "") {
      toast.error("Preencha emitente, período e horas abonadas.");
      return;
    }
    let arquivoUrl = ocorrenciaForm.arquivoUrl || null;
    try {
      if (ocorrenciaForm.arquivo) {
        if (ocorrenciaForm.arquivo.size > 50 * 1024 * 1024) throw new Error("Arquivo excede 50 MB");
        const formData = new FormData();
        formData.append("file", ocorrenciaForm.arquivo);
        const resposta = await fetch("/api/upload", { method: "POST", body: formData });
        if (!resposta.ok) throw new Error("Falha no upload");
        const dados = await resposta.json();
        arquivoUrl = dados.url;
      }
      const dados = {
        emitenteId: Number(ocorrenciaForm.emitenteId),
        funcionarioId: ocorrenciaForm.funcionarioId ? Number(ocorrenciaForm.funcionarioId) : null,
        tipo: ocorrenciaForm.tipo as any,
        dataInicio: ocorrenciaForm.dataInicio,
        dataFim: ocorrenciaForm.dataFim,
        horasAbonadas: Number(ocorrenciaForm.horasAbonadas),
        observacao: ocorrenciaForm.observacao || null,
        arquivoUrl,
      };
      if (ocorrenciaForm.id) {
        const { emitenteId: _emitenteId, funcionarioId: _funcionarioId, ...dadosEdicao } = dados;
        await atualizarOcorrenciaMut.mutateAsync({ id: ocorrenciaForm.id, ...dadosEdicao });
      } else {
        await criarOcorrenciaMut.mutateAsync(dados);
      }
    } catch (erro: any) {
      toast.error(erro.message ?? "Erro ao salvar ocorrência");
    }
  }

  function imprimirFechamento() {
    window.print();
  }

  function confirmarQuitacao() {
    if (!quitacaoObservacao.trim()) {
      toast.error("Informe o motivo da quitação.");
      return;
    }
    quitarMut.mutate({
      funcionarioId: Number(funcionarioFechamentoId),
      competencia,
      observacao: quitacaoObservacao.trim(),
    });
  }

  function abrirAjuste(registro: any) {
    setAjusteForm({
      registro,
      tipoAjuste: "corrigir",
      tipoCorrigido: registro.tipo,
      dataCorrigida: dataParaInput(registro.criadoEm),
      horaCorrigida: horaParaInput(registro.criadoEm),
      motivoAjuste: "erro_horario",
      detalhamentoAjuste: "",
      arquivo: null,
    });
    setAjusteModal(true);
  }

  function abrirCorrecaoPendente(pendencia: any) {
    const batidas = pendencia?.batidas ?? [];
    const contagemPorTipo = batidas.reduce((total: Record<string, number>, batida: any) => ({ ...total, [batida.tipo]: (total[batida.tipo] ?? 0) + 1 }), {});
    const faltantes = Object.fromEntries(Object.keys(TIPO_LABELS).filter((tipo) => !contagemPorTipo[tipo]).map((tipo) => [tipo, ""]));
    setCorrecaoPendenciaForm({
      pendencia,
      cargaHorariaDiaria: apuracao?.funcionario?.cargaHorariaDiaria != null ? String(apuracao.funcionario.cargaHorariaDiaria) : "",
      diasTrabalho: diasTrabalhoParaFormulario(apuracao?.funcionario?.diasTrabalho),
      registrosDesconsiderados: [],
      batidasFaltantes: faltantes,
      motivoAjuste: "erro_horario",
      detalhamentoAjuste: "",
    });
    setCorrecaoPendenciaModal(true);
  }

  function alternarRegistroDesconsiderado(registroId: number) {
    setCorrecaoPendenciaForm((form) => ({
      ...form,
      registrosDesconsiderados: form.registrosDesconsiderados.includes(registroId)
        ? form.registrosDesconsiderados.filter((id) => id !== registroId)
        : [...form.registrosDesconsiderados, registroId],
    }));
  }

  function alternarDiaTrabalho(dia: number) {
    setCorrecaoPendenciaForm((form) => ({
      ...form,
      diasTrabalho: form.diasTrabalho.includes(dia)
        ? form.diasTrabalho.filter((item) => item !== dia)
        : [...form.diasTrabalho, dia].sort((a, b) => a - b),
    }));
  }

  function salvarCorrecaoPendente() {
    if (!correcaoPendenciaForm.pendencia) return;
    if (correcaoPendenciaForm.pendencia.tipo === "jornada") {
      const cargaHorariaDiaria = Number(correcaoPendenciaForm.cargaHorariaDiaria);
      if (!Number.isFinite(cargaHorariaDiaria) || cargaHorariaDiaria <= 0 || correcaoPendenciaForm.diasTrabalho.length === 0) {
        toast.error("Informe a carga horária diária e os dias de trabalho.");
        return;
      }
      atualizarJornadaMut.mutate({
        id: Number(funcionarioFechamentoId),
        cargaHorariaDiaria,
        diasTrabalho: correcaoPendenciaForm.diasTrabalho,
      });
      return;
    }
    if (!correcaoPendenciaForm.detalhamentoAjuste.trim()) {
      toast.error("Informe o detalhamento da correção.");
      return;
    }
    const batidasFaltantes = Object.entries(correcaoPendenciaForm.batidasFaltantes).map(([tipo, horario]) => ({ tipo, horario }));
    if (batidasFaltantes.some((batida) => !batida.horario)) {
      toast.error("Preencha o horário de cada batida faltante.");
      return;
    }
    corrigirPendenciaMut.mutate({
      funcionarioId: Number(funcionarioFechamentoId),
      data: correcaoPendenciaForm.pendencia.data,
      registrosDesconsiderados: correcaoPendenciaForm.registrosDesconsiderados,
      batidasFaltantes: batidasFaltantes as any,
      motivoAjuste: correcaoPendenciaForm.motivoAjuste as any,
      detalhamentoAjuste: correcaoPendenciaForm.detalhamentoAjuste.trim(),
    });
  }

  async function salvarAjuste() {
    if (!ajusteForm.registro || !ajusteForm.detalhamentoAjuste.trim()) {
      toast.error("Informe o detalhamento do ajuste.");
      return;
    }
    try {
      let arquivoAjusteUrl: string | null = null;
      if (ajusteForm.arquivo) {
        if (ajusteForm.arquivo.size > 50 * 1024 * 1024) throw new Error("Arquivo excede 50 MB");
        const formData = new FormData();
        formData.append("file", ajusteForm.arquivo);
        const resposta = await fetch("/api/upload", { method: "POST", body: formData });
        if (!resposta.ok) throw new Error("Falha no upload");
        const dados = await resposta.json();
        arquivoAjusteUrl = dados.url;
      }
      await criarAjusteMut.mutateAsync({
        registroId: ajusteForm.registro.id,
        tipoAjuste: ajusteForm.tipoAjuste,
        tipoCorrigido: ajusteForm.tipoAjuste === "corrigir" ? ajusteForm.tipoCorrigido as any : undefined,
        dataCorrigida: ajusteForm.tipoAjuste === "corrigir" ? ajusteForm.dataCorrigida : undefined,
        horaCorrigida: ajusteForm.tipoAjuste === "corrigir" ? ajusteForm.horaCorrigida : undefined,
        motivoAjuste: ajusteForm.motivoAjuste as any,
        detalhamentoAjuste: ajusteForm.detalhamentoAjuste.trim(),
        arquivoAjusteUrl,
      });
    } catch (erro: any) {
      toast.error(erro.message ?? "Erro ao criar ajuste");
    }
  }

  return (
    <div className="p-6 space-y-6">
      <style>{`@media print { aside, .ponto-sem-impressao { display: none !important; } main { margin-left: 0 !important; padding: 0 !important; } body { background: white !important; } }`}</style>
      {/* Header */}
      <div className="ponto-sem-impressao">
        <h1 className="text-2xl font-bold text-gray-900">Controle de Ponto</h1>
        <p className="text-sm text-gray-500 mt-1">{total} registro{total !== 1 ? "s" : ""} encontrado{total !== 1 ? "s" : ""}</p>
      </div>

      <Tabs value={aba} onValueChange={setAba} className="space-y-6">
        <TabsList className="ponto-sem-impressao">
          <TabsTrigger value="registros">Registros</TabsTrigger>
          <TabsTrigger value="ocorrencias">Ocorrências</TabsTrigger>
          <TabsTrigger value="fechamento">Fechamento mensal</TabsTrigger>
        </TabsList>

        <TabsContent value="registros" className="space-y-6 mt-0 ponto-sem-impressao">
      {/* Filtros */}
      <div className="bg-white rounded-xl border p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex gap-2">
            <Input
              placeholder="Buscar por nome, matrícula ou CPF..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && buscar()}
              className="flex-1"
            />
            <Button onClick={buscar} variant="outline" size="icon">
              <Search className="w-4 h-4" />
            </Button>
          </div>
          <Select value={tipo} onValueChange={(v) => { setTipo(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-52">
              <SelectValue placeholder="Todos os tipos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os tipos</SelectItem>
              {Object.entries(TIPO_LABELS).map(([val, label]) => (
                <SelectItem key={val} value={val}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="space-y-1 flex-1">
            <Label className="text-xs text-gray-500">Data início</Label>
            <Input
              type="date"
              value={dataInicio}
              onChange={(e) => { setDataInicio(e.target.value); setPage(1); }}
            />
          </div>
          <div className="space-y-1 flex-1">
            <Label className="text-xs text-gray-500">Data fim</Label>
            <Input
              type="date"
              value={dataFim}
              onChange={(e) => { setDataFim(e.target.value); setPage(1); }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportarPdfRegistros} disabled={isLoading || total === 0 || exportando !== null}>
              <FileText className="w-4 h-4 mr-1" />{exportando === "pdf" ? "Exportando PDF..." : "Exportar PDF"}
            </Button>
            <Button variant="outline" onClick={exportarExcelRegistros} disabled={isLoading || total === 0 || exportando !== null}>
              <Download className="w-4 h-4 mr-1" />{exportando === "excel" ? "Exportando Excel..." : "Exportar Excel"}
            </Button>
          </div>
          <Button variant="ghost" onClick={limparFiltros} className="text-sm text-gray-500">
            Limpar filtros
          </Button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Matrícula</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Data / Hora</TableHead>
              <TableHead>Local</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-gray-400">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : registros.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-gray-400">
                  Nenhum registro encontrado
                </TableCell>
              </TableRow>
            ) : (
              registros.map((r: any) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.nome}</TableCell>
                  <TableCell>{r.matricula}</TableCell>
                  <TableCell>{formatarCpf(r.cpf)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TIPO_COLORS[r.tipo] ?? "bg-gray-100 text-gray-800"}`}>
                      {TIPO_LABELS[r.tipo] ?? r.tipo}
                    </span>
                  </TableCell>
                  <TableCell>
                    {r.sobrepostoPorId ? (
                      <Badge variant="secondary">Sobreposta pelo ajuste #{r.sobrepostoPorId}</Badge>
                    ) : r.origem === "ajuste" && r.tipoAjuste === "desconsiderar" ? (
                      <Badge variant="secondary">Desconsiderada</Badge>
                    ) : r.origem === "ajuste" ? (
                      <Badge>Aj. ativo</Badge>
                    ) : (
                      <Badge variant="outline">Original ativa</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{formatarDataHora(r.criadoEm)}</TableCell>
                  <TableCell className="text-xs text-gray-500 max-w-[180px] truncate">
                    {r.latitude && r.longitude
                      ? `${Number(r.latitude).toFixed(5)}, ${Number(r.longitude).toFixed(5)}`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {r.fotoUrl && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Abrir foto"
                        onClick={() => setFotoModal({ url: r.fotoUrl, nome: r.nome })}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    {!r.sobrepostoPorId && !(r.origem === "ajuste" && r.tipoAjuste === "desconsiderar") && (
                      <Button variant="ghost" size="icon" title="Ajustar batida" onClick={() => abrirAjuste(r)}>
                        <FilePenLine className="w-4 h-4" />
                      </Button>
                    )}
                    {!r.fotoUrl && r.sobrepostoPorId && <span className="text-gray-300">—</span>}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            Página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
        </TabsContent>

        <TabsContent value="ocorrencias" className="space-y-6 mt-0 ponto-sem-impressao">
          <div className="flex justify-end">
            <Button onClick={() => abrirNovaOcorrencia()}><Plus className="w-4 h-4 mr-1" /> Novo lançamento</Button>
          </div>
          <div className="bg-white rounded-xl border p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="space-y-1 flex-1">
                <Label className="text-xs text-gray-500">Funcionário</Label>
                <Select value={funcionarioOcorrenciaId} onValueChange={setFuncionarioOcorrenciaId}>
                  <SelectTrigger><SelectValue placeholder="Todos os funcionários" /></SelectTrigger>
                  <SelectContent><SelectItem value="todos">Todos os funcionários</SelectItem>{(funcionariosAtivos as any[]).map((funcionario) => <SelectItem key={funcionario.id} value={String(funcionario.id)}>{funcionario.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1 flex-1">
                <Label className="text-xs text-gray-500">Tipo</Label>
                <Select value={tipoOcorrenciaFiltro} onValueChange={setTipoOcorrenciaFiltro}>
                  <SelectTrigger><SelectValue placeholder="Todos os tipos" /></SelectTrigger>
                  <SelectContent><SelectItem value="todos">Todos os tipos</SelectItem>{Object.entries(TIPO_OCORRENCIA_LABELS).map(([valor, rotulo]) => <SelectItem key={valor} value={valor}>{rotulo}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="space-y-1 flex-1">
                <Label className="text-xs text-gray-500">Data início</Label>
                <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
              </div>
              <div className="space-y-1 flex-1">
                <Label className="text-xs text-gray-500">Data fim</Label>
                <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
              </div>
              <Button variant="ghost" onClick={limparFiltrosOcorrencias} className="text-sm text-gray-500">Limpar filtros</Button>
            </div>
          </div>
          <div className="bg-white rounded-xl border overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Funcionário</TableHead><TableHead>Tipo</TableHead><TableHead>Período</TableHead><TableHead>Horas abonadas</TableHead><TableHead>Observação</TableHead><TableHead>Documento</TableHead><TableHead>Criado por</TableHead><TableHead className="text-right">Ações</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(ocorrencias as any[]).length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-gray-400">Nenhuma ocorrência encontrada</TableCell></TableRow>
                ) : (ocorrencias as any[]).map((ocorrencia) => (
                  <TableRow key={ocorrencia.id}>
                    <TableCell className="font-medium">{ocorrencia.funcionarioNome ?? "Todos os funcionários"}</TableCell>
                    <TableCell><Badge variant="secondary">{TIPO_OCORRENCIA_LABELS[ocorrencia.tipo] ?? ocorrencia.tipo}</Badge></TableCell>
                    <TableCell className="text-sm text-gray-600">{ocorrencia.dataInicioTexto ?? String(ocorrencia.dataInicio).slice(0, 10)}{(ocorrencia.dataFimTexto ?? String(ocorrencia.dataFim).slice(0, 10)) !== (ocorrencia.dataInicioTexto ?? String(ocorrencia.dataInicio).slice(0, 10)) ? ` a ${ocorrencia.dataFimTexto ?? String(ocorrencia.dataFim).slice(0, 10)}` : ""}</TableCell>
                    <TableCell>{Number(ocorrencia.horasAbonadas ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}h/dia</TableCell>
                    <TableCell className="max-w-[240px] truncate">{ocorrencia.observacao || "—"}</TableCell>
                    <TableCell>{ocorrencia.arquivoUrl ? <a className="text-sm text-blue-600 hover:underline" href={ocorrencia.arquivoUrl} target="_blank" rel="noreferrer">Abrir</a> : "—"}</TableCell>
                    <TableCell className="text-sm text-gray-600">{ocorrencia.criadoPorNome ?? "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => abrirNovaOcorrencia(ocorrencia)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { if (confirm("Excluir esta ocorrência?")) excluirOcorrenciaMut.mutate({ id: ocorrencia.id }); }}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="fechamento" className="space-y-6 mt-0">
          {apuracao && <div className="hidden print:block"><h1 className="text-2xl font-bold">Espelho Mensal de Ponto</h1><p className="text-sm">{apuracao.funcionario.nome} · Matrícula {apuracao.funcionario.matricula} · Competência {competencia}</p></div>}
          <div className="bg-white rounded-xl border p-4 space-y-4 ponto-sem-impressao">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="space-y-1 flex-1"><Label className="text-xs text-gray-500">Competência</Label><Input type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} /></div>
              <div className="space-y-1 flex-[2]"><Label className="text-xs text-gray-500">Funcionário</Label>
                <Select value={funcionarioFechamentoId} onValueChange={setFuncionarioFechamentoId}><SelectTrigger><SelectValue placeholder="Selecione o funcionário" /></SelectTrigger><SelectContent>{(funcionariosAtivos as any[]).map((funcionario) => <SelectItem key={funcionario.id} value={String(funcionario.id)}>{funcionario.nome}</SelectItem>)}</SelectContent></Select>
              </div>
              <Button variant="outline" onClick={() => { refetchApuracao(); refetchFechamento(); }} disabled={!funcionarioFechamentoId}>Calcular</Button>
            </div>
          </div>
          {!funcionarioFechamentoId ? (
            <div className="bg-white rounded-xl border p-10 text-center text-gray-400">Selecione um funcionário e uma competência para apurar o mês.</div>
          ) : apuracao ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ponto-sem-impressao">
                <div>
                  <p className="font-medium">{apuracao.funcionario.nome} · {competencia}</p>
                  <p className="text-sm text-gray-500">{fechamentoSalvo?.status === "fechado" ? "Competência fechada" : "Competência em aberto"}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={imprimirFechamento}><Printer className="w-4 h-4 mr-1" /> Imprimir / PDF</Button>
                  {fechamentoSalvo?.status === "fechado" ? (
                    fechamentoSalvo?.quitadoEm ? (
                      <Badge variant="secondary" className="h-10 px-3">Saldo quitado</Badge>
                    ) : (
                      <>
                        <Button variant="outline" onClick={() => { setQuitacaoObservacao(""); setQuitacaoModal(true); }} disabled={Number(fechamentoSalvo?.bancoAtualMinutos ?? 0) === 0}>Quitar saldo</Button>
                        <Button variant="outline" onClick={() => reabrirMut.mutate({ funcionarioId: Number(funcionarioFechamentoId), competencia })}>Reabrir mês</Button>
                      </>
                    )
                  ) : (
                    <Button onClick={() => fecharMut.mutate({ funcionarioId: Number(funcionarioFechamentoId), competencia })} disabled={apuracao.pendencias.length > 0}>Fechar mês</Button>
                  )}
                </div>
              </div>
              {apuracao.pendencias.length > 0 && <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800 ponto-sem-impressao"><span><strong>Fechamento bloqueado:</strong> há {apuracao.pendencias.length} pendência(s). Corrija as batidas incompletas ou duplicadas, ou lance um abono integral para o dia.</span><Button size="sm" variant="outline" onClick={() => abrirCorrecaoPendente(apuracao.pendencias[0])}>Corrigir</Button></div>}
              {fechamentoSalvo?.quitadoEm && <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 ponto-sem-impressao"><strong>Saldo quitado:</strong> {formatarHoras(fechamentoSalvo.quitadoMinutos)} em {formatarDataHora(fechamentoSalvo.quitadoEm)}. Esta competência não pode ser reaberta.</div>}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-white rounded-xl border p-4"><p className="text-xs text-gray-500">Previstas</p><p className="text-lg font-semibold">{formatarHoras(apuracao.totais.previstasMinutos)}</p></div>
                <div className="bg-white rounded-xl border p-4"><p className="text-xs text-gray-500">Batidas</p><p className="text-lg font-semibold">{formatarHoras(apuracao.totais.batidasMinutos)}</p></div>
                <div className="bg-white rounded-xl border p-4"><p className="text-xs text-gray-500">Abonadas</p><p className="text-lg font-semibold">{formatarHoras(apuracao.totais.abonadasMinutos)}</p></div>
                <div className="bg-white rounded-xl border p-4"><p className="text-xs text-gray-500">Saldo do mês</p><p className="text-lg font-semibold">{formatarHoras(apuracao.totais.saldoMinutos)}</p></div>
                <div className="bg-white rounded-xl border p-4"><p className="text-xs text-gray-500">Extras</p><p className="text-lg font-semibold text-green-700">{formatarHoras(apuracao.totais.extrasMinutos)}</p></div>
                <div className="bg-white rounded-xl border p-4"><p className="text-xs text-gray-500">Faltas</p><p className="text-lg font-semibold text-red-700">{formatarHoras(-apuracao.totais.faltasMinutos)}</p></div>
                <div className="bg-white rounded-xl border p-4"><p className="text-xs text-gray-500">Banco anterior</p><p className="text-lg font-semibold">{formatarHoras(fechamentoSalvo?.bancoAnteriorMinutos ?? 0)}</p></div>
                <div className="bg-white rounded-xl border p-4"><p className="text-xs text-gray-500">Banco atual</p><p className="text-lg font-semibold">{formatarHoras(fechamentoSalvo?.bancoAtualMinutos ?? apuracao.totais.saldoMinutos)}</p></div>
              </div>
              <div className="bg-white rounded-xl border overflow-hidden">
                <Table><TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Status</TableHead><TableHead>Previstas</TableHead><TableHead>Batidas</TableHead><TableHead>Abonadas</TableHead><TableHead>Extras</TableHead><TableHead>Faltas</TableHead><TableHead>Motivo</TableHead><TableHead className="ponto-sem-impressao">Ações</TableHead></TableRow></TableHeader><TableBody>
                  {apuracao.dias.map((dia: any) => <TableRow key={dia.data}><TableCell>{dia.data}</TableCell><TableCell><Badge variant={dia.status === "pendente" ? "destructive" : dia.status === "apurado" ? "secondary" : "outline"}>{dia.status === "pendente" ? "Pendente" : dia.status === "apurado" ? "Apurado" : "Fora da jornada"}</Badge></TableCell><TableCell>{formatarHoras(dia.previstasMinutos)}</TableCell><TableCell>{formatarHoras(dia.batidasMinutos)}</TableCell><TableCell>{formatarHoras(dia.abonadasMinutos)}</TableCell><TableCell>{formatarHoras(dia.extrasMinutos)}</TableCell><TableCell>{formatarHoras(-dia.faltasMinutos)}</TableCell><TableCell className="text-sm text-gray-600">{dia.motivo ?? "—"}</TableCell><TableCell className="ponto-sem-impressao">{dia.status === "pendente" && <Button size="sm" variant="outline" onClick={() => abrirCorrecaoPendente(dia)}>Corrigir</Button>}</TableCell></TableRow>)}
                </TableBody></Table>
              </div>
            </>
          ) : null}
        </TabsContent>
      </Tabs>

      <Dialog open={quitacaoModal} onOpenChange={setQuitacaoModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Quitar saldo do banco de horas</DialogTitle>
          </DialogHeader>
          <div className="rounded-lg border bg-muted/30 p-4 text-sm space-y-1">
            <p><strong>Funcionário:</strong> {apuracao?.funcionario?.nome ?? "—"}</p>
            <p><strong>Competência:</strong> {competencia}</p>
            <p><strong>Saldo a quitar integralmente:</strong> {formatarHoras(fechamentoSalvo?.bancoAtualMinutos ?? 0)}</p>
          </div>
          <p className="text-sm text-muted-foreground">A quitação zera integralmente o banco a partir desta competência, recalcula os meses fechados posteriores e bloqueia a reabertura deste mês.</p>
          <div className="space-y-1"><Label>Motivo da quitação</Label><Textarea rows={4} value={quitacaoObservacao} onChange={(e) => setQuitacaoObservacao(e.target.value)} placeholder="Ex.: saldo compensado ou pago na folha." /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setQuitacaoModal(false)}>Cancelar</Button>
            <Button onClick={confirmarQuitacao} disabled={quitarMut.isPending}>{quitarMut.isPending ? "Quitando..." : "Quitar saldo integral"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Foto */}
      <Dialog open={!!fotoModal} onOpenChange={() => setFotoModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{fotoModal?.nome}</DialogTitle>
          </DialogHeader>
          {fotoModal?.url && (
            <img
              src={fotoModal.url}
              alt={fotoModal.nome}
              className="w-full rounded-lg object-cover"
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={ajusteModal} onOpenChange={(aberto) => setAjusteModal(aberto)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajustar batida de ponto</DialogTitle>
          </DialogHeader>
          {ajusteForm.registro && (
            <>
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
                <p className="font-medium">Batida original — preservada no histórico</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-muted-foreground">
                  <span><strong className="text-foreground">Funcionário:</strong> {ajusteForm.registro.nome} · {ajusteForm.registro.matricula}</span>
                  <span><strong className="text-foreground">ID:</strong> #{ajusteForm.registro.id}</span>
                  <span><strong className="text-foreground">Tipo:</strong> {TIPO_LABELS[ajusteForm.registro.tipo]}</span>
                  <span><strong className="text-foreground">Data/hora:</strong> {formatarDataHora(ajusteForm.registro.criadoEm)}</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Ação</Label>
                <Select value={ajusteForm.tipoAjuste} onValueChange={(tipoAjuste) => setAjusteForm((form) => ({ ...form, tipoAjuste: tipoAjuste as "corrigir" | "desconsiderar" }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corrigir">Corrigir batida</SelectItem>
                    <SelectItem value="desconsiderar">Desconsiderar batida</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{ajusteForm.tipoAjuste === "corrigir" ? "Cria uma nova batida que substitui a original somente na apuração." : "Mantém a batida no histórico, mas a retira da apuração sem criar outra batida válida."}</p>
              </div>
              {ajusteForm.tipoAjuste === "corrigir" && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1"><Label>Tipo corrigido</Label>
                    <Select value={ajusteForm.tipoCorrigido} onValueChange={(tipoCorrigido) => setAjusteForm((form) => ({ ...form, tipoCorrigido }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(TIPO_LABELS).map(([valor, rotulo]) => <SelectItem key={valor} value={valor}>{rotulo}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label>Data corrigida</Label><Input type="date" value={ajusteForm.dataCorrigida} onChange={(e) => setAjusteForm((form) => ({ ...form, dataCorrigida: e.target.value }))} /></div>
                  <div className="space-y-1"><Label>Hora corrigida</Label><Input type="time" value={ajusteForm.horaCorrigida} onChange={(e) => setAjusteForm((form) => ({ ...form, horaCorrigida: e.target.value }))} /></div>
                </div>
              )}
              <div className="space-y-1"><Label>Motivo</Label>
                <Select value={ajusteForm.motivoAjuste} onValueChange={(motivoAjuste) => setAjusteForm((form) => ({ ...form, motivoAjuste }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(MOTIVO_AJUSTE_LABELS).map(([valor, rotulo]) => <SelectItem key={valor} value={valor}>{rotulo}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Detalhamento do ajuste</Label><Textarea rows={4} value={ajusteForm.detalhamentoAjuste} onChange={(e) => setAjusteForm((form) => ({ ...form, detalhamentoAjuste: e.target.value }))} placeholder="Explique o que ocorreu e como o horário foi confirmado." /></div>
              <div className="space-y-2">
                <Label>Documento</Label>
                <Input type="file" onChange={(e) => setAjusteForm((form) => ({ ...form, arquivo: e.target.files?.[0] ?? null }))} />
                <p className="text-xs text-muted-foreground">Opcional. Limite de 50 MB. O ajuste não recebe foto ou GPS próprios; a evidência da batida original é mantida separadamente.</p>
              </div>
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">A batida original não será editada nem excluída. O ajuste ficará identificado com autor, data de criação, motivo e vínculo com a batida original.</div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAjusteModal(false)}>Cancelar</Button>
                <Button onClick={salvarAjuste} disabled={criarAjusteMut.isPending}>{criarAjusteMut.isPending ? "Salvando..." : "Criar ajuste"}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={correcaoPendenciaModal} onOpenChange={setCorrecaoPendenciaModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Corrigir pendência do fechamento</DialogTitle>
          </DialogHeader>
          {correcaoPendenciaForm.pendencia && (
            <>
              <div className="rounded-lg border bg-muted/30 p-4 text-sm space-y-1">
                <p><strong>Funcionário:</strong> {apuracao?.funcionario?.nome ?? "—"}</p>
                <p><strong>Competência:</strong> {competencia}</p>
                {correcaoPendenciaForm.pendencia.data && <p><strong>Data:</strong> {correcaoPendenciaForm.pendencia.data}</p>}
                <p><strong>Pendência:</strong> {correcaoPendenciaForm.pendencia.motivo}</p>
              </div>

              {correcaoPendenciaForm.pendencia.tipo === "jornada" ? (
                <div className="space-y-4">
                  <div className="space-y-1">
                    <Label>Carga horária diária</Label>
                    <Input type="number" min="0.01" max="24" step="0.01" value={correcaoPendenciaForm.cargaHorariaDiaria} onChange={(e) => setCorrecaoPendenciaForm((form) => ({ ...form, cargaHorariaDiaria: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Dias de trabalho</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {DIAS_TRABALHO_LABELS.map((rotulo, dia) => <label key={rotulo} className="flex items-center gap-2 rounded border p-2 text-sm"><input type="checkbox" checked={correcaoPendenciaForm.diasTrabalho.includes(dia)} onChange={() => alternarDiaTrabalho(dia)} />{rotulo}</label>)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(TIPO_LABELS).map(([tipo, rotulo]) => {
                    const batidasTipo = (correcaoPendenciaForm.pendencia.batidas ?? []).filter((batida: any) => batida.tipo === tipo);
                    if (batidasTipo.length === 0) {
                      return <div key={tipo} className="space-y-1"><Label>{rotulo}</Label><Input type="time" value={correcaoPendenciaForm.batidasFaltantes[tipo] ?? ""} onChange={(e) => setCorrecaoPendenciaForm((form) => ({ ...form, batidasFaltantes: { ...form.batidasFaltantes, [tipo]: e.target.value } }))} /><p className="text-xs text-muted-foreground">Batida ausente: informe o horário confirmado.</p></div>;
                    }
                    if (batidasTipo.length === 1) {
                      return <div key={tipo} className="rounded border p-3 text-sm"><strong>{rotulo}:</strong> {batidasTipo[0].horario}</div>;
                    }
                    return <div key={tipo} className="space-y-2 rounded border border-yellow-200 bg-yellow-50 p-3"><Label>{rotulo} — escolha quais batidas duplicadas devem ser desconsideradas</Label>{batidasTipo.map((batida: any) => <label key={batida.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={correcaoPendenciaForm.registrosDesconsiderados.includes(batida.id)} onChange={() => alternarRegistroDesconsiderado(batida.id)} />Desconsiderar #{batida.id} às {batida.horario}</label>)}</div>;
                  })}
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">As batidas originais não são apagadas. As duplicadas escolhidas são desconsideradas na apuração e as faltantes são registradas como ajuste, com autor e data.</div>
                  <div className="space-y-1"><Label>Motivo</Label><Select value={correcaoPendenciaForm.motivoAjuste} onValueChange={(motivoAjuste) => setCorrecaoPendenciaForm((form) => ({ ...form, motivoAjuste }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(MOTIVO_AJUSTE_LABELS).map(([valor, rotulo]) => <SelectItem key={valor} value={valor}>{rotulo}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-1"><Label>Detalhamento da correção</Label><Textarea rows={4} value={correcaoPendenciaForm.detalhamentoAjuste} onChange={(e) => setCorrecaoPendenciaForm((form) => ({ ...form, detalhamentoAjuste: e.target.value }))} placeholder="Explique como os horários foram confirmados." /></div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setCorrecaoPendenciaModal(false)}>Cancelar</Button>
                <Button onClick={salvarCorrecaoPendente} disabled={atualizarJornadaMut.isPending || corrigirPendenciaMut.isPending}>{atualizarJornadaMut.isPending || corrigirPendenciaMut.isPending ? "Salvando..." : "Salvar correção"}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={ocorrenciaModal} onOpenChange={(aberto) => setOcorrenciaModal(aberto)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{ocorrenciaForm.id ? "Editar ocorrência" : "Novo lançamento de ocorrência"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1"><Label>Emitente</Label>
              <Select value={ocorrenciaForm.emitenteId} onValueChange={(emitenteId) => setOcorrenciaForm((form) => ({ ...form, emitenteId }))} disabled={!!ocorrenciaForm.id}>
                <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger><SelectContent>{(emitentes as any[]).map((emitente) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.nomeFantasia || emitente.razaoSocial}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Funcionário</Label>
              <Select value={ocorrenciaForm.funcionarioId || "todos"} onValueChange={(valor) => {
                const funcionario = (funcionariosAtivos as any[]).find((item) => item.id === Number(valor));
                setOcorrenciaForm((form) => ({ ...form, funcionarioId: valor === "todos" ? "" : valor, emitenteId: valor === "todos" ? form.emitenteId : String(funcionario?.emitenteId ?? form.emitenteId) }));
              }} disabled={!!ocorrenciaForm.id}>
                <SelectTrigger><SelectValue placeholder="Todos os funcionários" /></SelectTrigger><SelectContent><SelectItem value="todos">Todos os funcionários</SelectItem>{(funcionariosAtivos as any[]).map((funcionario) => <SelectItem key={funcionario.id} value={String(funcionario.id)}>{funcionario.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Tipo</Label>
              <Select value={ocorrenciaForm.tipo} onValueChange={(tipo) => setOcorrenciaForm((form) => ({ ...form, tipo }))}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(TIPO_OCORRENCIA_LABELS).map(([valor, rotulo]) => <SelectItem key={valor} value={valor}>{rotulo}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Horas abonadas por dia</Label><Input type="number" min="0" max="24" step="0.25" value={ocorrenciaForm.horasAbonadas} onChange={(e) => setOcorrenciaForm((form) => ({ ...form, horasAbonadas: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Data inicial</Label><Input type="date" value={ocorrenciaForm.dataInicio} onChange={(e) => setOcorrenciaForm((form) => ({ ...form, dataInicio: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Data final</Label><Input type="date" value={ocorrenciaForm.dataFim} onChange={(e) => setOcorrenciaForm((form) => ({ ...form, dataFim: e.target.value }))} /></div>
          </div>
          <div className="space-y-1"><Label>Observação</Label><Textarea rows={4} value={ocorrenciaForm.observacao} onChange={(e) => setOcorrenciaForm((form) => ({ ...form, observacao: e.target.value }))} /></div>
          <div className="space-y-2">
            <Label>Documento</Label>
            {ocorrenciaForm.arquivoUrl && <a className="block text-sm text-blue-600 hover:underline" href={ocorrenciaForm.arquivoUrl} target="_blank" rel="noreferrer">Abrir documento atual</a>}
            <Input type="file" onChange={(e) => setOcorrenciaForm((form) => ({ ...form, arquivo: e.target.files?.[0] ?? null }))} />
            <p className="text-xs text-gray-500">O envio usa o mesmo upload do cadastro de funcionário, com limite de 50 MB.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOcorrenciaModal(false)}>Cancelar</Button>
            <Button onClick={salvarOcorrencia}><Upload className="w-4 h-4 mr-1" /> Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
